import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ModalProvider } from './contexts/ModalContext';
import { db } from './firebase/config';
import { collection, query, where, onSnapshot, getDocs, limit } from 'firebase/firestore';
import { Toaster, toast } from 'react-hot-toast';
import Router from './router/Router';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import ModalManager from './components/ModalManager';

import HomePage from './pages/HomePage';
import CreatePostPage from './pages/CreatePostPage';
import PostPage from './pages/PostPage';
import ChatsListPage from './pages/ChatsListPage';
import ChatPage from './pages/ChatPage';
import FriendsPage from './pages/FriendsPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';
import ErrorBoundary from './components/ErrorBoundary';

import { soundPlayer } from './utils/audio';
import { requestNotificationPermission, sendBrowserNotification } from './utils/notifications';
import { getChatUnreadCount, markChatAsRead } from './firebase/helpers';
import { X } from 'lucide-react';




const AppContent = () => {
    const { currentUser } = useAuth();
    const { isDark } = useTheme();
    const [page, setPage] = useState({ name: 'home' });
    const [unreadChatCount, setUnreadChatCount] = useState(0);

    const unreadChatCountRef = useRef(0);
    useEffect(() => {
        unreadChatCountRef.current = unreadChatCount;
    }, [unreadChatCount]);

    // Request browser notification permission on mount
    useEffect(() => {
        requestNotificationPermission();
    }, []);

    const navigate = useCallback((pageName, params = {}) => {
        // Search profile by username resolution
        if (pageName === 'search-profile' && params.username) {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('username', '==', params.username), limit(1));
            getDocs(q).then(snap => {
                if (!snap.empty) {
                    setPage({ name: 'profile', userId: snap.docs[0].id });
                } else {
                    toast.error(`User @${params.username} not found.`);
                }
            }).catch(() => toast.error("Error looking up user."));
            return;
        }

        // When entering a chat, immediately mark unread count as zero
        if (pageName === 'chat' && params.chatId && currentUser) {
            markChatAsRead(params.chatId, currentUser.uid);
        }

        setPage({ name: pageName, ...params });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentUser]);

    // Real-time unread messages listener + sound chime
    const prevUnreadMapRef = useRef({});
    const isFirstRunRef = useRef(true);

    useEffect(() => {
        if (!currentUser) return;
        const q = query(collection(db, 'chats'), where('participants', 'array-contains', currentUser.uid));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let totalUnreadChats = 0;
            let shouldChime = false;
            let senderName = 'Someone';
            let messageSnippet = 'New message';

            snapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                const chatId = docSnap.id;
                const isInsideThisChat = page.name === 'chat' && page.chatId === chatId;

                // If user is currently viewing this exact chat, immediately reset its unread count
                if (isInsideThisChat) {
                    markChatAsRead(chatId, currentUser.uid);
                }

                const unread = isInsideThisChat ? 0 : getChatUnreadCount(data, currentUser.uid);
                const prevUnread = prevUnreadMapRef.current[chatId] || 0;

                if (unread > 0) {
                    totalUnreadChats += 1;
                }

                // If unread count increased for this chat
                if (unread > prevUnread && !isInsideThisChat) {
                    shouldChime = true;
                    const otherUid = data.participants?.find(p => p !== currentUser.uid);
                    const otherProfile = data.participantProfiles?.[otherUid] || {};
                    senderName = otherProfile.name || otherProfile.username || 'Someone';
                    messageSnippet = data.lastMessage?.text || 'Sent you a message';
                }

                prevUnreadMapRef.current[chatId] = unread;
            });

            setUnreadChatCount(totalUnreadChats);

            if (isFirstRunRef.current) {
                isFirstRunRef.current = false;
                return;
            }

            if (shouldChime) {
                const soundPref = localStorage.getItem('batcheet_sound_enabled') !== 'false';
                if (soundPref) {
                    soundPlayer.playNotificationSound();
                }
                sendBrowserNotification(`Message from ${senderName}`, { body: messageSnippet });
                toast(`New message from ${senderName}! 💬`, { icon: '💬' });
            }
        }, (err) => console.error("Unread chats error:", err));

        return () => unsubscribe();
    }, [currentUser, page.name, page.chatId]);

    const renderPage = () => {
        switch (page.name) {
            case 'create':
                return <CreatePostPage navigate={navigate} />;
            case 'post':
                return <PostPage navigate={navigate} postId={page.postId} />;
            case 'friends':
                return <FriendsPage navigate={navigate} />;
            case 'chats':
                return <ChatsListPage navigate={navigate} />;
            case 'chat':
                return <ChatPage navigate={navigate} chatId={page.chatId} initialPrompt={page.initialPrompt} />;
            case 'profile':
                return <ProfilePage navigate={navigate} userId={page.userId} />;
            case 'my-profile':
                return <ProfilePage navigate={navigate} userId={currentUser?.uid} />;
            case 'notifications':
                return <NotificationsPage navigate={navigate} />;
            case 'settings':
                return <SettingsPage navigate={navigate} />;
            case 'home':
            default:
                return <HomePage navigate={navigate} />;
        }
    };

    const isChatPage = page.name === 'chat';

    return (
        <div className={`min-h-screen bg-slate-50 dark:bg-[#0b0f17] text-slate-900 dark:text-slate-100 transition-colors duration-200 font-sans antialiased ${isChatPage ? 'h-[100dvh] overflow-hidden' : 'pb-24 sm:pb-28'}`}>
            {/* Top-Center Interactive, Auto-Dismissing Notification Toast Banner */}
            <Toaster
                position="top-center"
                gutter={8}
                containerStyle={{
                    top: 16,
                }}
                toastOptions={{
                    duration: 2500,
                    style: {
                        background: isDark ? 'rgba(17, 24, 39, 0.96)' : 'rgba(255, 255, 255, 0.96)',
                        color: isDark ? '#f8fafc' : '#0f172a',
                        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        borderRadius: '1.25rem',
                        boxShadow: isDark ? '0 12px 30px -4px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)' : '0 12px 30px -4px rgba(0,0,0,0.12)',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        padding: '10px 14px',
                        maxWidth: '92vw',
                        cursor: 'pointer',
                    }
                }}
            >
                {(t) => (
                    <div
                        onClick={() => toast.dismiss(t.id)}
                        className="flex items-center gap-2.5 w-full select-none cursor-pointer group"
                        title="Click to dismiss"
                    >
                        <div className="flex-1 flex items-center gap-2">
                            {t.icon}
                            <span className="text-xs font-semibold leading-tight break-words">
                                {typeof t.message === 'function' ? t.message(t) : t.message}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toast.dismiss(t.id);
                            }}
                            className="p-1 -mr-1 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors opacity-70 group-hover:opacity-100"
                            title="Dismiss"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </Toaster>


            <ModalManager />

            {/* Sticky Header with 1-Tap Dark/Light Switch & Universal Search (Hidden in Chat view to give full screen to conversation) */}
            {!isChatPage && <Header navigate={navigate} />}

            {/* Mobile-First Centered Social Feed Container */}
            <main className={isChatPage ? 'max-w-xl mx-auto h-[100dvh] w-full flex flex-col p-0 sm:py-2' : 'max-w-xl mx-auto px-3 sm:px-4 pt-3.5 sm:pt-5 w-full'}>
                {renderPage()}
            </main>

            {/* Floating Mobile Dock Navigation (Instagram/Twitter styled) */}
            <BottomNav
                navigate={navigate}
                currentPage={page.name}
                unreadChatCount={unreadChatCount}
            />
        </div>
    );
};

function App() {
    return (
        <ErrorBoundary>
            <ThemeProvider>
                <AuthProvider>
                    <ModalProvider>
                        <Router>
                            <AppContent />
                        </Router>
                    </ModalProvider>
                </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
